import get from 'lodash/get'
import { ApiError } from '../../error-handling/throwifApiError'

type ErrorMessageLike = { error: string } | { message?: string } | { suggestion?: string } | undefined | string

/**
 * Parses an API response for its error details, returning it as an Error.
 *
 * For 404 and 401 responses, throws an ApiError with the appropriate type
 * so that isNotFoundApiError / isAuthApiError type guards match.
 */
export const captureApiError = ({
  response,
  errorDetail,
  errorMessageLike,
  legacyStringResponse,
}: {
  response: Response
  errorMessageLike: ErrorMessageLike
  errorDetail: unknown
  legacyStringResponse?: string
}) => {
  const { status: statusCode, statusText } = response
  const errorMessage = parseErrorMessage(errorMessageLike)
  const message = legacyStringResponse ?? errorMessage ?? 'Non OK response from fetch'

  if (statusCode === 404) {
    const detail = get(errorDetail, 'detail', {})
    throw new ApiError({ type: 'NotFoundError', detail, message })
  }

  if (statusCode === 401) {
    throw new ApiError({ type: 'UnauthorisedError', detail: {}, message })
  }

  return new Error(errorMessage || statusText)
}

function parseErrorMessage(error: ErrorMessageLike): string | undefined {
  if (error === undefined) return undefined
  if (typeof error === 'string') return error
  if ('error' in error) return error.error
  if ('message' in error) return error.message
  if ('suggestion' in error) return error.suggestion
  return undefined
}
